import { createFileRoute } from '@tanstack/react-router';
import { Text } from '@mantine/core';
import { IconClockCheck } from '@tabler/icons-react';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { BillingStatusBadge } from '@/features/timesheet/BillingStatusBadge';
import { useAbility } from '@/app/providers/AbilityProvider';
import { formatDate, formatNumber } from '@/shared/lib/format';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Entry extends Denormalized {
  type: 'timesheet-entries';
  date?: string;
  ordinaryHours?: number | null;
  overtimeHours?: number | null;
  billingStatus?: number;
  description?: string;
  notes?: string;
}

export const Route = createFileRoute('/_app/timesheet/approval')({ component: ApprovalPage });

function ApprovalPage() {
  const ability = useAbility();
  const canEditBilling = ability.can('update', 'Timesheet') || ability.can('manage', 'all');
  return (
    <ResourceListPage<Entry>
      title=""
      description="Timesheet in attesa di approvazione (PM)"
      type="timesheet-entries"
      columns={[
        {
          accessorKey: 'date',
          header: 'Data',
          Cell: ({ cell }) => <Text size="sm">{formatDate(cell.getValue<string>())}</Text>,
        },
        {
          accessorKey: 'ordinaryHours',
          header: 'Ore ordinarie',
          mantineTableBodyCellProps: { align: 'right' },
          mantineTableHeadCellProps: { align: 'right' },
          Cell: ({ cell }) => <Text size="sm">{formatNumber(cell.getValue<number>() ?? 0, 1)} h</Text>,
        },
        {
          accessorKey: 'overtimeHours',
          header: 'Straordinari',
          mantineTableBodyCellProps: { align: 'right' },
          mantineTableHeadCellProps: { align: 'right' },
          Cell: ({ cell }) => <Text size="sm">{formatNumber(cell.getValue<number>() ?? 0, 1)} h</Text>,
        },
        {
          accessorKey: 'billingStatus',
          header: 'Stato',
          size: 150,
          enableColumnFilter: false,
          Cell: ({ row }) => (
            <BillingStatusBadge
              entryId={row.original.id}
              currentStatus={row.original.billingStatus ?? 0}
              canEdit={canEditBilling}
              invalidateKeys={[['jsonapi', 'timesheet-entries'], ['timesheet']]}
            />
          ),
        },
        { accessorKey: 'description', header: 'Descrizione' },
        { accessorKey: 'notes', header: 'Note' },
      ]}
      searchField="notes"
      emptyMessage="Nessun timesheet pending"
      emptyDescription="Le registrazioni timesheet in attesa di approvazione appariranno qui."
      emptyIcon={IconClockCheck}
    />
  );
}
